import dayjs from "dayjs";
import { type JwtPayload } from "jwt-decode";

import {
    SubscribableAssetsService,
    UserDetailsService,
    VerificationService,
    type SubscribableAsset,
    type UserDetails,
} from "@hsl/lgim-explorer/src/openapi";

import {
    type Store,
    type SubscribableAssetAPIDocument,
    type SubscribableAssetAPIResponse,
    type UpdatesMap,
    type User,
} from "./types";

export async function verifyUser(
    userId?: string | null,
    inviteId?: string | null,
): Promise<{ success: false } | { success: true; token: string | null }> {
    if (!userId || !inviteId) {
        return { success: false };
    }
    try {
        const verification = await VerificationService.verifyFundCentreUser(
            userId,
            inviteId,
        );
        return { success: true, token: verification?.token ?? null };
    } catch (err) {
        console.error(err);
        return { success: false };
    }
}

type UserSubscriptionData = {
    user?: Required<UserDetails> | null;
    userSubscriptions: Required<SubscribableAsset>[];
    success: boolean;
};

export async function fetchUserSubscriptionData(
    token: Required<JwtPayload>,
): Promise<UserSubscriptionData> {
    try {
        const { sub } = token;
        const user = (await UserDetailsService.getUser(sub)) as Store["user"];
        const userSubscriptions =
            (await SubscribableAssetsService.getSubscriptions(sub).catch(
                (err) => {
                    console.error(err);
                    return [];
                },
            )) as Store["userSubscriptions"];
        return { user, userSubscriptions, success: true };
    } catch (err) {
        console.log("LOGIN ERROR", err);
        return { user: null, userSubscriptions: [], success: false };
    }
}

export async function fetchUser(
    token: Required<JwtPayload>,
    userType: User["userType"] = "new",
): Promise<User | null> {
    try {
        const user = (await UserDetailsService.getUser(
            token.sub,
        )) as Required<UserDetails>;
        return { ...user, userType };
    } catch (err) {
        console.log("LOGIN ERROR", err);
        return null;
    }
}

export async function fetchSubscriptions(
    token: Required<JwtPayload>,
): Promise<Required<SubscribableAsset>[]> {
    try {
        return (await SubscribableAssetsService.getSubscriptions(
            token.sub,
        )) as Store["userSubscriptions"];
    } catch (err) {
        console.log("Error fetching user subscriptions", err);
        return [];
    }
}

export async function obtainToken(
    userId?: string | null,
    inviteId?: string | null,
    storedToken?: string | null,
) {
    if (storedToken) {
        return { token: storedToken, dach: false };
    }
    const vRes = await verifyUser(userId, inviteId);
    if (vRes.success) {
        return { token: vRes.token, dach: !vRes.token };
    } else {
        return { dach: false, token: null };
    }
}

export async function getSubscriptionUpdateData(
    user: User,
    subscriptions: SubscribableAsset[],
): Promise<UpdatesMap> {
    try {
        if (subscriptions.length === 0) {
            return {};
        }
        const quasarToId = (quasar: string) =>
            subscriptions
                .find((sub) => sub.entityId === quasar)
                ?.fundId?.split("-")[1];
        const lastLogin = dayjs(user.lastViewedDateTime);
        const params = new URLSearchParams(
            subscriptions
                .filter((s) => s.subscriptionStatus === "CONFIRMED")
                .map((s) => ["id", s.subscriptionId!])
                .concat([["language", user.language]]),
        );
        const res = await fetch(
            `/srp/api/subscribable-assets?${params.toString()}`,
        );
        const json = (await res.json()) as SubscribableAssetAPIResponse;
        const updatedDocs: Array<
            SubscribableAssetAPIDocument & {
                fundId?: string;
                subsId: string;
                shareClassInstanceId?: string;
            }
        > = json.data.flatMap((d) =>
            d.documents
                .filter(
                    (doc) =>
                        dayjs(doc.updated_at).isAfter(lastLogin) &&
                        doc.instance_id > 1,
                )
                .map((doc) => ({
                    ...doc,
                    subsId: d.id,
                    fundId: quasarToId(doc.fund_identifier),
                })),
        );
        const nonKidFactDocs = updatedDocs.filter(
            (doc) => !doc.share_class_identifier,
        );
        const kidFactDocs = updatedDocs
            .filter((doc) => Boolean(doc.share_class_identifier))
            .map((doc) => ({
                ...doc,
                shareClassInstanceId: /kii?d/gi.test(doc.subsId)
                    ? `kiid-${doc.share_class_identifier}`
                    : `factsheet-${doc.share_class_identifier}`,
            }));
        return [...kidFactDocs, ...nonKidFactDocs].reduce(
            (updMap, { instance_id, fundId, shareClassInstanceId }) => {
                return fundId
                    ? {
                          ...updMap,
                          [fundId]: [
                              ...(updMap[fundId] ?? []),
                              shareClassInstanceId ?? instance_id,
                          ],
                      }
                    : updMap;
            },
            {} as UpdatesMap,
        );
    } catch (err) {
        console.error(err);
        return {};
    }
}
